import type { ColumnProps } from 'antd/lib/table'
import { parseISO, isPast } from 'date-fns'
import { noop, groupBy } from 'lodash'
import moment from 'moment'
import { type RouteComponentProps, withRouter } from 'react-router'
import { Box, Text } from '@weareberyl/design-system'

import { defaultData } from './data'

import { formatDatetime } from '~/utils'
import { withScheme } from '~/components/Scheme'
import Table from '~/components/Table'
import * as routes from '~/constants/routes'
import {
  useUserProductListQuery,
  ProductType,
  type UserProductListQuery_user_User_table_UserProductConnection_nodes_UserProduct as UserProductListItem,
} from '~/gql/generated/graphql'
import { SCHEME } from '~/types'

const isInactive = (userProduct: UserProductListItem): boolean => {
  const dt = parseISO(userProduct.valid_until)
  if (isPast(dt)) {
    return true
  }
  const hasBalance = !!userProduct.balances?.current_balance?.amount
  const isPeriodPass =
    userProduct.scheme_product.product_type === ProductType.period_pass
  return !hasBalance && isPeriodPass
}

const Usage = ({
  used,
  limit,
  period,
  label,
}: {
  used: number
  limit: number
  period: string
  label: string
}) => {
  const usage = [...Array(limit).keys()]
    .reverse()
    .map(index => (index < used ? '⚪️' : '🟢'))
    .join(' ')

  const formattedPeriod = moment.duration(period).humanize().replace('a ', '') // e.g. "a day" -> "day"
  const text = `${used} used, out of ${limit} for last ${formattedPeriod}`

  return (
    <Box key={limit} title={text}>
      {label}: {usage}
      <Text variant="micro">{text}</Text>
    </Box>
  )
}

const columns: ColumnProps<UserProductListItem>[] = [
  {
    title: 'ID',
    dataIndex: 'id',
  },
  {
    title: 'Date/Time',
    dataIndex: 'created_at',
    render: text => formatDatetime(text),
  },
  {
    title: 'Name',
    dataIndex: ['scheme_product', 'name'],
  },
  {
    title: 'Valid Until',
    dataIndex: 'valid_until',
    render: text => formatDatetime(text),
  },
  {
    title: 'Pass Minutes',
    dataIndex: ['balances', 'current_balance', 'formatted_amount'],
  },
  {
    title: 'Total Pass Minutes',
    dataIndex: ['balances', 'total_balance', 'formatted_amount'],
  },
  {
    title: 'Caps',
    render: (data: UserProductListItem) => {
      if (data.billable_journey_cap) {
        const { used, limit, period } = data.billable_journey_cap
        return (
          <Usage
            used={used}
            limit={limit}
            period={period}
            label="Billable journeys"
          />
        )
      }
      if (data.period_pass_unlocks) {
        // Group by limit just in case that is configured differently for a particular hardware_type
        return Object.entries(
          groupBy(data.period_pass_unlocks, value => value.limit),
        ).map(
          ([hardware_type, value]: [
            string,
            UserProductListItem['period_pass_unlocks'],
          ]) => {
            // Get the first one, as the group members should have the same limit for all hardware types
            if (!value?.[0]) {
              return null
            }
            const { used, limit, period } = value[0]
            return (
              <Usage
                key={hardware_type}
                used={used}
                limit={limit}
                period={period}
                label="Included unlocks"
              />
            )
          },
        )
      }
      return null
    },
  },
  {
    title: 'Bonus?',
    dataIndex: 'is_bonus',
    align: 'center',
    render: is_bonus => (is_bonus ? '🌟' : ''),
  },
]

const UserProducts = ({
  scheme,
  user_id,
  history,
}: {
  scheme: SCHEME
  user_id: string
} & RouteComponentProps) => {
  const variables = {
    scheme_id: scheme.id,
    user_id,
    order_by: [{ id: 'desc' }],
  }
  const { data, loading } = useUserProductListQuery({
    variables,
    errorPolicy: 'all',
  })

  // If it loads defaultData then that won't be null!
  const tableData = (data?.user ??
    defaultData.user) as (typeof defaultData)['user']

  return (
    <Table<UserProductListItem>
      columns={columns}
      data={tableData}
      loading={loading}
      onChange={noop}
      rowClassName={(record: UserProductListItem) =>
        isInactive(record) ? 'inactive-row' : 'generic'
      }
      onRow={(record: UserProductListItem) => {
        return {
          onClick: () => {
            history.push(
              routes.prependScheme(
                `${routes.PRODUCTS}/${record.scheme_product.id}`,
                record.scheme_product.scheme_id,
              ),
            )
          },
        }
      }}
    />
  )
}

export default withRouter(withScheme(UserProducts))
